import { useState } from "react";
import { motion } from "framer-motion";
import { Heart, Loader2, Lock, QrCode, ShieldCheck, Users, Zap } from "lucide-react";
import { toast } from "sonner";
import ProgressBar from "./ProgressBar";
import CountUp from "./CountUp";
import ShareButtons from "./ShareButtons";
import { createDonation, formatBRL } from "@/lib/api";

const PRESETS = [1, 5, 25, 50, 100, 500];

export default function DonationPanel({ campaign, onDonationCreated }) {
  const [amount, setAmount] = useState(1);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const pct = ((amount - 1) / 499) * 100;

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await createDonation(campaign.slug, {
        amount_cents: amount * 100,
        donor_name: name,
        message,
      });
      onDonationCreated({ ...res, donorName: name || "Anônimo" });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Não foi possível iniciar a doação. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <aside
      data-testid="donation-panel"
      className="space-y-5 rounded-[1.75rem] border border-[#EFE9DF] bg-white p-6 shadow-xl shadow-[#FF6D47]/5 lg:sticky lg:top-24"
    >
      <div>
        <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-[#9E9085]">Arrecadado</p>
        <p className="font-display mt-1 text-4xl font-extrabold text-[#FF6D47]">
          <CountUp value={campaign.raised_cents} format={(v) => formatBRL(Math.round(v))} />
        </p>
        <p className="mt-1 text-sm font-bold text-[#6E6259]">de {formatBRL(campaign.goal_cents)}</p>
        <ProgressBar pct={campaign.progress} className="mt-4" />
        <p className="mt-2 text-right text-xs font-extrabold text-[#059669]">{campaign.progress}% da meta</p>
        <div className="mt-4 flex items-center justify-between border-t border-[#EFE9DF] pt-4 text-sm font-bold text-[#6E6259]">
          <span className="flex items-center gap-1.5">
            <Heart className="h-4 w-4 fill-[#E11D48] text-[#E11D48]" /> {campaign.hearts} corações
          </span>
          <span className="flex items-center gap-1.5">
            <Users className="h-4 w-4" /> {campaign.supporters} apoiadores
          </span>
        </div>
      </div>

      <div className="space-y-4 border-t border-[#EFE9DF] pt-5">
        <p className="font-display font-extrabold text-[#261C14]">Escolha o valor da sua doação</p>
        <div className="grid grid-cols-3 gap-2">
          {PRESETS.map((v) => (
            <motion.button
              key={v}
              type="button"
              data-testid={`donation-amount-preset-${v}`}
              whileTap={{ scale: 0.94 }}
              onClick={() => setAmount(v)}
              className={`h-11 rounded-xl border-2 text-sm font-extrabold transition ${
                amount === v
                  ? "border-[#FF6D47] bg-[#FF6D47] text-white shadow-[0_6px_16px_rgba(255,109,71,0.4)]"
                  : "border-[#EFE9DF] bg-white text-[#261C14] hover:border-[#FF6D47] hover:text-[#FF6D47]"
              }`}
            >
              R$ {v}
            </motion.button>
          ))}
        </div>

        <div>
          <div className="flex items-end justify-between">
            <span className="text-xs font-extrabold uppercase tracking-wider text-[#9E9085]">Você vai doar</span>
            <span className="font-display text-3xl font-extrabold text-[#FF6D47]">R$ {amount}</span>
          </div>
          <input
            type="range"
            min="1"
            max="500"
            step="1"
            value={amount}
            data-testid="donation-slider"
            onChange={(e) => setAmount(Number(e.target.value))}
            className="donation-slider mt-3"
            style={{ "--fill": `${pct}%` }}
            aria-label="Escolha o valor entre 1 e 500 reais"
          />
          <div className="mt-1 flex justify-between text-[11px] font-bold text-[#9E9085]">
            <span>R$ 1</span>
            <span>R$ 500</span>
          </div>
        </div>

        <input
          type="number"
          min="1"
          max="500"
          value={amount}
          data-testid="donation-custom-input"
          onChange={(e) => {
            const v = Number(e.target.value);
            if (!Number.isNaN(v) && v > 0) setAmount(Math.min(500, Math.max(1, v)));
          }}
          className="h-12 w-full rounded-xl border-2 border-[#EFE9DF] bg-[#F7F3ED] px-4 text-sm font-extrabold outline-none transition focus:border-[#FF6D47] focus:bg-white"
          aria-label="Digite outro valor entre 1 e 500"
        />

        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="donation-donor-name-input"
          maxLength={60}
          placeholder="Seu nome (opcional)"
          className="h-12 w-full rounded-xl border-2 border-[#EFE9DF] bg-[#F7F3ED] px-4 text-sm font-semibold outline-none transition focus:border-[#FF6D47] focus:bg-white"
        />
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          data-testid="donation-donor-message-input"
          maxLength={280}
          rows={2}
          placeholder="Mensagem de carinho (opcional)"
          className="w-full resize-none rounded-xl border-2 border-[#EFE9DF] bg-[#F7F3ED] p-4 text-sm font-semibold outline-none transition focus:border-[#FF6D47] focus:bg-white"
        />
      </div>

      <div className="space-y-3">
        <motion.button
          type="button"
          data-testid="donation-submit-pix-btn"
          whileTap={{ scale: 0.97 }}
          onClick={submit}
          disabled={submitting}
          className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[#FF6D47] text-lg font-extrabold text-white shadow-[0_10px_30px_rgba(255,109,71,0.4)] transition hover:bg-[#E85732] disabled:opacity-70"
        >
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <QrCode className="h-5 w-5" />}
          {submitting ? "Gerando Pix..." : "Doar agora via Pix"}
        </motion.button>

        <ShareButtons title={campaign.title} />

        <div className="flex items-center justify-between gap-2 pt-1 text-[11px] font-bold text-[#9E9085]">
          <span className="flex items-center gap-1">
            <ShieldCheck className="h-3.5 w-3.5 text-[#059669]" /> Doação segura
          </span>
          <span className="flex items-center gap-1">
            <Lock className="h-3.5 w-3.5" /> SSL
          </span>
          <span className="flex items-center gap-1">
            <Zap className="h-3.5 w-3.5 text-[#32BCAD]" /> Pix · LivePix
          </span>
        </div>
      </div>
    </aside>
  );
}
